import React, { useEffect, useMemo } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { fetchConversations } from "src/redux/actions/conversation";

import { Col, Dropdown, Row } from "react-bootstrap";
import { SortByAction } from "./components/sortBy";
import Actions from "src/components/actionbar";
import ConversationsList from "./components/list";
import ConversationDetail from "./components/detail";

import _ from "lodash";
import cx from "classnames";
import styles from "./conversations.module.scss";
import { useContentMapAdoption } from "src/redux/actions/adoption/contentMap";

function Conversations() {
  //selectors
  const { conversations, deletions, loading } = useSelector((state: any) => {
    return {
      conversations: state.conversations.conversations.items,
      deletions: state.conversations.delete,
      loading: state.conversations.conversations.loading,
    };
  });

  //constants
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { id } = useParams();
  const [nextId, setNextId] = React.useState<string | null>(null);
  const sortOptions = [
    {
      label: "Created Date",
      value: "created_date",
    },
    {
      label: "Last Activity",
      value: "activity_date",
    },
    {
      label: "Time Spent",
      value: "time_spent",
    },
  ];
  const viewerTypeOptions = [
    {
      value: "all",
      label: "All Viewers",
    },
    {
      value: "known",
      label: "Known Viewers",
    },
    {
      value: "anonymous",
      label: "Anonymous Viewers",
    },
  ];

  //mixpanel events
  const { trackContentSearched } = useContentMapAdoption();

  //side effects
  useEffect(() => {
    dispatch(
      fetchConversations({
        sort: sortPreferances.sortBy,
        order: sortPreferances.orderBy,
        type: viewerTypePreference.value,
        query,
        page: 0,
      }),
    );
  }, []);

  useEffect(() => {
    if (_.size(conversations) > 0 && !id) {
      navigate(`/conversations/${conversations[0].id}`);
    }
  }, [conversations]);

  useEffect(() => {
    if (_.size(conversations) > 0 && id) {
      navigate(`/conversations/${id}`);
    }
  }, [conversations, id]);

  useEffect(() => {
    if (_.get(deletions, `${id}.deleting`)) {
      const nextIndex =
        conversations.findIndex((conversation: any) => conversation.id === id) +
        1;
      setNextId(conversations[nextIndex]?.id || null);
    }

    if (_.get(deletions, `${id}.deleted`)) {
      if (nextId) {
        navigate(`/conversations/${nextId}`);
      } else {
        navigate(`/conversations`);
      }
    }
  }, [_.get(deletions, `${id}`)]);

  //states
  const [query, setQuery] = React.useState("");
  const [sortPreferances, setSortPreferances] = React.useState({
    sortBy: "activity_date",
    orderBy: "DESC",
  });
  const [viewerTypePreference, setViewerTypePreferance] = React.useState({
    value: "all",
    label: "All Viewers",
  });

  //functions
  const handleSearch = (queryString: string | null) => {
    navigate(`/conversations`);
    setQuery(queryString || "");
    dispatch(
      fetchConversations({
        sort: sortPreferances.sortBy,
        order: sortPreferances.orderBy,
        type: viewerTypePreference.value,
        query: queryString || "",
        page: 0,
      }),
    );

    if (queryString) {
      trackContentSearched({
        searchString: queryString,
        sourceCta: "conversations",
        filter: "",
        searchTags: [],
        categories: [],
      });
    }
  };

  const handleSortChange = (value: { sortBy: string; orderBy: string }) => {
    navigate(`/conversations`);
    dispatch(
      fetchConversations({
        sort: value.sortBy,
        order: value.orderBy,
        type: viewerTypePreference.value,
        query,
        page: 0,
      }),
    );
    setSortPreferances(value);
  };

  const handleViewerTypeChange = (value: { value: string; label: string }) => {
    navigate(`/conversations`);
    dispatch(
      fetchConversations({
        sort: sortPreferances.sortBy,
        order: sortPreferances.orderBy,
        type: value.value,
        query,
        page: 0,
      }),
    );
    setViewerTypePreferance(value);
  };

  const loadMoreConversations = (pageNo: number) => {
    dispatch(
      fetchConversations({
        sort: sortPreferances.sortBy,
        order: sortPreferances.orderBy,
        type: viewerTypePreference.value,
        query,
        page: pageNo,
      }),
    );
  };

  //render
  return (
    <Row className={styles.container}>
      <Col xl={4} xxl={4} className={styles.listContainer}>
        <section className={styles.listSection}>
          <div className={styles.header}>
            <h2 className={styles.title}>conversations</h2>
            <p className={styles.subTitle}>track user engagement</p>
          </div>
          <div className={styles.searchContainer}>
            <Actions.Search query={query} onSearch={handleSearch} disabled={loading} />
            <div className={styles.filterContainer}>
              <ViewerTypeDropDown
                selected={viewerTypePreference}
                onChange={handleViewerTypeChange}
                options={viewerTypeOptions}
              />
              <SortByAction
                value={sortPreferances}
                onChange={handleSortChange}
                sortOptions={sortOptions}
              />
            </div>
          </div>
          <ConversationsList
            sortBy={sortPreferances.sortBy}
            loadMore={loadMoreConversations}
          />
        </section>
      </Col>
      <Col xl={8} xxl={8} className={styles.detailContainer}>
        <section className={styles.detailSection}>
          <ConversationDetail
            id={id}
            selectedFilter={viewerTypePreference.value}
          />
        </section>
      </Col>
    </Row>
  );
}

export default Conversations;

interface IViewerTypeDropDown {
  selected: {
    value: string;
    label: string;
  };
  options: Array<{ value: string; label: string; icon?: string }>;
  onChange: ({ value, label }: { value: string; label: string }) => void;
}
export const ViewerTypeDropDown: React.FC<IViewerTypeDropDown> = ({
  selected,
  options,
  onChange,
}) => {
  const selectedOption = useMemo(() => {
    return options.find((option) => option.value === selected.value);
  }, [selected, options]);

  const [show, setShow] = React.useState(false);

  const handleSelect = (value: string) => {
    if (onChange) {
      onChange({
        value,
        label: options.find((option) => option.value === value)?.label || "",
      });
    }
  };

  return (
    <Dropdown className={styles.selectDropdown}>
      <span onClick={() => setShow(!show)}>
        <Dropdown.Toggle>
          {selectedOption && <>{selectedOption?.label}</>}
          {_.size(options) > 1 && (
            <i
              className={cx("paperflite-dropdown", {
                [styles.openModal]: show,
              })}
            ></i>
          )}
        </Dropdown.Toggle>
      </span>

      {_.size(options) > 1 && (
        <Dropdown.Menu
          popperConfig={{
            modifiers: [
              {
                name: "offset",
                options: {
                  offset: [0, 6],
                },
              },
            ],
          }}
        >
          {options.map((option, index) => {
            const selected = option.value === selectedOption?.value;
            return (
              <Dropdown.Item
                key={index}
                className={cx({ [styles.active]: selected })}
                onClick={() => handleSelect(option.value)}
              >
                <span>{option.label}</span>
              </Dropdown.Item>
            );
          })}
        </Dropdown.Menu>
      )}
    </Dropdown>
  );
};
